import styled from "@emotion/styled";
import { LoadingContainer } from "@ui/components/common/loading-container";
import React, { ImgHTMLAttributes, useEffect, useRef, useState } from "react";

export const ImageWithLoading: React.FC<ImgHTMLAttributes<HTMLImageElement>> = ({ src, ...props }) => {
	const [isLoading, setIsLoading] = useState(true);
	const imgRef = useRef<HTMLImageElement | null>(null);

	useEffect(() => {
		let cancelled = false;
		setIsLoading(true);
		if (!src) {
			setIsLoading(false);
			return () => {
				cancelled = true;
			};
		}
		const currentImg = imgRef.current;
		if (currentImg?.complete && currentImg.naturalWidth > 0) {
			setIsLoading(false);
			return () => {
				cancelled = true;
			};
		}
		const pre = document.createElement("img");
		pre.onload = () => {
			if (cancelled) return;
			setIsLoading(false);
		};
		pre.onerror = () => {
			if (cancelled) return;
			setIsLoading(false);
		};
		pre.src = src;
		return () => {
			cancelled = true;
			pre.onload = null;
			pre.onerror = null;
		};
	}, [src]);

	const handleLoad = () => setIsLoading(false);
	const handleError = () => setIsLoading(false);

	return (
		<ImageContainer>
			<Image
				ref={imgRef}
				src={src}
				isLoading={isLoading}
				onLoad={handleLoad}
				onError={handleError}
				{...props}
			/>
			{isLoading && <LoadingContainer />}
		</ImageContainer>
	);
};

const ImageContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
`;

const Image = styled.img<{ isLoading: boolean }>`
	width: 100%;
	display: ${({ isLoading }) => (isLoading ? "none" : "block")};
`;
